import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SalesDetailPage from './SalesDetailPage';
import { PAYOUTS_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/payoutsColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { buildPayoutsFilterGroups } from '../constants/payoutsFiltersConfig';

const SOURCE_BADGES = {
  'DOS': { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
  'Advance': { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  'Opendate Distribution': { bg: 'var(--sl-bg-warning-primary, #fff4e5)', color: 'var(--sl-text-warning, #b54708)' },
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.source && activeFilters.source.length > 0) {
    params.source = activeFilters.source;
  }
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.payout_scope && activeFilters.payout_scope.length > 0) {
    params.payout_scope = activeFilters.payout_scope[0];
  }
  return params;
};

const PAYOUTS_CONFIG = {
  pageTitle: 'Payouts',
  pageSubtitle: 'Pending payouts broken down by source across your venues.',
  sidebarKey: 'finances_payouts',
  defaultPreset: 'this_month',
  dateModeOptions: [
    { key: 'event_date', label: 'Event Date' },
    { key: 'order_date', label: 'Sold Date' },
    { key: 'payout_date', label: 'Payout Date' },
  ],
  defaultSortRules: [{ field: 'sold_date', direction: 'desc' }],

  columns: PAYOUTS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingFinancesPayouts_${teamId}`,
  firstColumnId: 'event_name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/payouts/data.json`,
  detailResponseKey: 'rows',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/payouts/export`,
  detailErrorMessage: 'Failed to load payouts data.',
  detailTableTitle: 'Pending Payouts',
  emptyMessage: 'No pending payouts found.',
  searchPlaceholder: 'Search payouts...',
  paginationLabel: 'payouts',
  itemLabel: (count) => `payout${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  renderNotice: (activeFilters) => {
    const eligibleOnly = activeFilters?.payout_scope?.includes('eligible');
    if (!eligibleOnly) return null;
    return (
      <div
        className="alert d-flex align-items-center"
        style={{ background: 'var(--sl-bg-brand-primary, #eff8ff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', color: 'var(--sl-text-secondary, #344054)', fontSize: 13, borderRadius: 8 }}
      >
        <i className="fas fa-info-circle mr-2" style={{ color: 'var(--sl-fg-brand, #175cd3)' }} />
        Opendate Distribution (held) funds are excluded here — they aren't eligible for payout until released. Switch off this filter to see held balances.
      </div>
    );
  },

  buildKpiCards: (current, prior) => [
    { label: 'Total Pending', value: compactCurrency(current.total_pending || 0), current: current.total_pending || 0, prior: prior.total_pending || 0 },
    { label: 'DOS', value: compactCurrency(current.dos_pending || 0), current: current.dos_pending || 0, prior: prior.dos_pending || 0 },
    { label: 'Advance', value: compactCurrency(current.advance_pending || 0), current: current.advance_pending || 0, prior: prior.advance_pending || 0 },
    { label: 'Opendate Distribution', value: compactCurrency(current.distribution_pending || 0), current: current.distribution_pending || 0, prior: prior.distribution_pending || 0 },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  topCharts: [],

  groupByOptions: [
    { key: 'transaction', label: 'By Transaction' },
    { key: 'event', label: 'By Event' },
  ],
  groupByDefault: 'event',

  eventGroupedColumns: [
    { key: 'event_name', label: 'Event' },
    { key: 'venue_name', label: 'Venue' },
    { key: 'event_date', label: 'Event Date' },
    { key: 'dos', label: 'DOS' },
    { key: 'advance', label: 'Advance' },
    { key: 'distribution', label: 'Opendate Distribution' },
    { key: 'total', label: 'Total Pending' },
  ],

  renderEventCell: (colKey, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (colKey) {
      case 'event_name': return row.event_name || '—';
      case 'venue_name': return row.venue_name || '—';
      case 'event_date': return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '—'}</span>;
      case 'dos': return cf.format(row.dos || 0);
      case 'advance': return cf.format(row.advance || 0);
      case 'distribution': return cf.format(row.distribution || 0);
      case 'total': return <strong>{cf.format(row.total || 0)}</strong>;
      default: return '—';
    }
  },

  renderCellValue: (col, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'event_name': return row.event_name || '—';
      case 'venue_name': return row.venue_name || '—';
      case 'source': {
        const badge = SOURCE_BADGES[row.source] || SOURCE_BADGES['Advance'];
        return row.source
          ? <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>{row.source}</span>
          : '—';
      }
      case 'amount': return cf.format(row.amount || 0);
      case 'sold_date': return <span className="text-nowrap">{row.sold_date ? formatEventDate(row.sold_date) : '—'}</span>;
      case 'event_date': return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '—'}</span>;
      case 'payout_date': return <span className="text-nowrap">{row.payout_date ? formatEventDate(row.payout_date) : '—'}</span>;
      case 'order_id': return row.order_id || '—';
      case 'order_email': return row.order_email || '—';
      default: return '—';
    }
  },

  filterTitle: 'Filter Payouts',
  filterSubtitle: 'Apply filters to narrow pending payout data.',
};

function ReportingFinancesPayoutsPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/finances/payouts/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((error) => console.error('Failed to load payouts filter options', error));
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...PAYOUTS_CONFIG,
    buildFilterGroups: () => buildPayoutsFilterGroups(filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesPayoutsPage;
